/* @jsx jsx */
import { css, jsx, useTheme } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import type { Theme } from '@coursera/cds-core';
import { Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { SuccessIcon } from '@coursera/cds-icons';

import AllPlansCourseraLiteEnrollButton, {
  handleCreateCourseraLiteCart,
} from 'bundles/coursera-plus-landing-page/components/priceComparison/AllPlansCourseraLiteEnrollButton';
import AllPlansCourseraPlusEnrollButton, {
  handleCreateCourseraPlusCart,
} from 'bundles/coursera-plus-landing-page/components/priceComparison/AllPlansCourseraPlusEnrollButton';
import { useGetSubscriptionTiersProductPrices } from 'bundles/coursera-plus-landing-page/contentfulData/providerHOC';
import { useProductVariantUrlAction } from 'bundles/coursera-plus-landing-page/utils/useProductVariantUrlAction';
import type { ComparisonProduct } from 'bundles/coursera-plus/components/subscriptionTiers/tiersDesignV3/ComparisonTable';
import { ComparisonTableComponent } from 'bundles/coursera-plus/components/subscriptionTiers/tiersDesignV3/ComparisonTable';
import { getFeatureTableModalCopy } from 'bundles/coursera-plus/components/subscriptionTiers/tiersDesignV3/HeroAndCompareAllPlansCopy';
import type { SubscriptionTiersProductVariant } from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import {
  courseraLiteProductVariants,
  courseraPlusProductVariants,
} from 'bundles/coursera-plus/constants/CourseraPlusProductVariant';
import type { PropsFromWithCourseraLiteCreateCartMutation } from 'bundles/coursera-plus/utils/withCourseraLiteCreateCartMutation';
import { withCourseraLiteCreateCartMutation } from 'bundles/coursera-plus/utils/withCourseraLiteCreateCartMutation';
import { ENROLL } from 'bundles/enroll/utils/enrollActionParams';
import { SIGNUP_FORM_KEY } from 'bundles/user-account/common/constants';

import _t from 'i18n!nls/coursera-plus-landing-page';

const styles = {
  wrapper: css`
    padding: 32px;
    margin: 32px auto;
  `,
  container: css`
    max-width: 1152px;
    margin: 32px auto;
  `,
  row: () => css`
    display: flex;
    align-items: center;
    margin-bottom: var(--cds-spacing-150);
    gap: var(--cds-spacing-100);

    &:last-child {
      margin-bottom: var(--cds-spacing-600);

      ${breakpoints.down('xs')} {
        margin-bottom: var(--cds-spacing-300);
      }
    }
  `,
  sectionHeader: (_: Theme, hasSubTitle: boolean) => css`
    text-align: center;
    margin-bottom: ${hasSubTitle ? 'var(--cds-spacing-200)' : 'var(--cds-spacing-600)'};
  `,
  subHeader: () => css`
    text-align: center;
    margin-bottom: var(--cds-spacing-400);
  `,
  button: () => css`
    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: 100%;
    }
  `,
  valueProps: css`
    list-style: none;
    margin: 0;
    padding: 0;
  `,
  icon: () => css`
    min-width: 16px;
  `,
  ctaSection: () => css`
    text-align: center;
    margin-top: var(--cds-spacing-600);
  `,
};

const PLUS_ANNUAL_VARIANT = courseraPlusProductVariants.ANNUAL_SEVEN_DAY_FREE_TRIAL;
const PLUS_MONTHLY_VARIANT = courseraPlusProductVariants.MONTHLY_WITH_FREE_TRIAL;
const LITE_MONTHLY_VARIANT = courseraLiteProductVariants.MONTHLY_WITH_FREE_TRIAL;

const getComparisonProductFromVariant = (variant: SubscriptionTiersProductVariant): ComparisonProduct => {
  if (variant === LITE_MONTHLY_VARIANT) {
    return 'LITE';
  } else if (variant === PLUS_MONTHLY_VARIANT) {
    return 'PLUS';
  } else if (variant === PLUS_ANNUAL_VARIANT) {
    return 'PLUS_ANNUAL';
  }
  throw new Error(`Invalid subscription tiers variant ${variant}}`);
};

type PropsFromCaller = {
  userHasCourseraLiteFreeTrial: boolean;
  userHasCourseraPlusFreeTrial: boolean;
  isCourseraLiteLandingPageEnabled: boolean;
};

export type PropsToComponent = PropsFromCaller & PropsFromWithCourseraLiteCreateCartMutation;

const LandingPageComparisonTableContainer = ({
  userHasCourseraLiteFreeTrial,
  userHasCourseraPlusFreeTrial,
  isCourseraLiteLandingPageEnabled,
  createCourseraLiteCart,
}: PropsToComponent) => {
  const router = useRouter();
  const { action, variant: selectedVariant, ...queryRest } = useProductVariantUrlAction();
  const theme = useTheme();
  const comparisonTableRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [productItemId, setProductItemId] = useState<SubscriptionTiersProductVariant>(LITE_MONTHLY_VARIANT);
  const { courseraLiteProductPrice, courseraPlusProductPrice, courseraPlusAnnualProductPrice } =
    useGetSubscriptionTiersProductPrices({});
  const [selectedProduct, setSelectedProduct] = useState<ComparisonProduct>('LITE');
  const copy = getFeatureTableModalCopy({
    hasFreeTrialForLite: userHasCourseraLiteFreeTrial,
    hasFreeTrialForPlus: userHasCourseraPlusFreeTrial,
  });

  const showAuthModal = () => {
    if (!user.isAuthenticatedUser()) {
      router.replace({
        pathname: router.location.pathname,
        query: { ...router.location.query, authMode: SIGNUP_FORM_KEY, action: ENROLL, selectedVariant: productItemId },
      });
    }
  };

  const handleSetSelectedProduct = (product: ComparisonProduct) => {
    setSelectedProduct(product);
    if (product === 'PLUS_ANNUAL') {
      setProductItemId(PLUS_ANNUAL_VARIANT);
    } else if (product === 'PLUS') {
      setProductItemId(PLUS_MONTHLY_VARIANT);
    } else if (product === 'LITE') {
      setProductItemId(LITE_MONTHLY_VARIANT);
    }
  };
  const isLoggedIn = user.isAuthenticatedUser();

  useEffect(() => {
    if (user.isAuthenticatedUser() && action === ENROLL && selectedVariant) {
      comparisonTableRef?.current?.scrollIntoView?.();
      setIsLoading(true);
      setSelectedProduct(getComparisonProductFromVariant(selectedVariant as SubscriptionTiersProductVariant));
      setProductItemId(selectedVariant as SubscriptionTiersProductVariant);
      // Remove action param and replace browser history to prevent checkout redirect
      // from happening again if user decides to press the back button from checkout
      router.replace({
        pathname: router.location.pathname,
        query: queryRest,
      });
      if (selectedVariant === LITE_MONTHLY_VARIANT && isCourseraLiteLandingPageEnabled) {
        handleCreateCourseraLiteCart(createCourseraLiteCart);
      } else if ([PLUS_ANNUAL_VARIANT, PLUS_MONTHLY_VARIANT].includes(selectedVariant)) {
        const liteVariant = selectedVariant ?? productItemId;
        handleCreateCourseraPlusCart(liteVariant as SubscriptionTiersProductVariant);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoggedIn,
    productItemId,
    selectedProduct,
    createCourseraLiteCart,
    router,
    isCourseraLiteLandingPageEnabled,
    selectedVariant,
    action,
  ]);

  const EnrollButton = selectedProduct === 'LITE' ? AllPlansCourseraLiteEnrollButton : AllPlansCourseraPlusEnrollButton;

  if (!courseraLiteProductPrice || !courseraPlusProductPrice || !courseraPlusAnnualProductPrice) {
    return null;
  }

  let enrollCTACopy: string | undefined;
  const exhaustedCTA = _t('Renew Subscription');
  const trialCTA = _t('Start 7-day free trial');
  let sectionHeader = _t('Explore more subscriptions');
  let subHeader: string | undefined;
  const hasLiteAndPlusFreeTrials = userHasCourseraLiteFreeTrial && userHasCourseraPlusFreeTrial;
  if (hasLiteAndPlusFreeTrials) {
    sectionHeader = _t('Choose a subscription plan');
    subHeader = _t('Get a 7-day free trial');
  }

  if (selectedProduct === 'LITE') {
    enrollCTACopy = userHasCourseraLiteFreeTrial ? trialCTA : exhaustedCTA;
  } else if (selectedProduct === 'PLUS') {
    enrollCTACopy = userHasCourseraPlusFreeTrial ? trialCTA : exhaustedCTA;
  } else if (selectedProduct === 'PLUS_ANNUAL') {
    enrollCTACopy = userHasCourseraPlusFreeTrial ? trialCTA : exhaustedCTA;
  }

  return (
    <div css={styles.wrapper} ref={comparisonTableRef}>
      <div css={styles.container}>
        <Typography variant="d2semibold" css={styles.sectionHeader(theme, hasLiteAndPlusFreeTrials)}>
          {sectionHeader}
        </Typography>
        {subHeader && (
          <Typography2 component="h3" variant="subtitleMedium" css={styles.subHeader}>
            {subHeader}
          </Typography2>
        )}
        <ul css={styles.valueProps}>
          <li css={styles.row}>
            <SuccessIcon color="interactive" size="small" css={styles.icon} />
            <Typography2 component="p" color="supportText">
              {copy.valueProp1}
            </Typography2>
          </li>
          <li css={styles.row}>
            <SuccessIcon color="interactive" size="small" css={styles.icon} />
            <Typography2 component="p" color="supportText">
              {copy.valueProp2}
            </Typography2>
          </li>
          <li css={styles.row}>
            <SuccessIcon color="interactive" size="small" css={styles.icon} />
            <Typography2 component="p" color="supportText">
              {copy.valueProp3}
            </Typography2>
          </li>
        </ul>

        <ComparisonTableComponent
          courseraLiteProductPrice={courseraLiteProductPrice}
          courseraPlusProductPrice={courseraPlusProductPrice}
          courseraPlusAnnualProductPrice={courseraPlusAnnualProductPrice}
          onSelect={handleSetSelectedProduct}
          defaultSelectedProduct={selectedProduct}
          isFullWidth={true}
        />

        <div css={styles.ctaSection}>
          <EnrollButton
            size="medium"
            variant="primary"
            css={styles.button}
            trackingName="subscription_tiers_all_plans_modal_cta_landing_page"
            data-testid="subscription-tiers-all-plans-modal-cta-landing-page"
            showAuthModal={showAuthModal}
            productItemId={productItemId}
            loading={isLoading}
          >
            {enrollCTACopy}
          </EnrollButton>
        </div>
      </div>
    </div>
  );
};

export default withCourseraLiteCreateCartMutation(LandingPageComparisonTableContainer);
